<template>
  <loader :is-loading="loading || isSubmitting" />
  <div v-if="!loading" class="flex flex-col space-y-5 bg-white p-8 shadow-xs">
    <div class="relative flex justify-between text-left">
      <div class="flex w-full items-center justify-between">
        <div class="flex max-w-1/2 items-center space-x-4">
          <back-button :route="backRoute" />
          <div class="truncate">
            <lf-h1 v-tooltip="activeEmailTemplate?.name">
              <template v-if="activeEmailTemplate?.name">
                {{
                  $t("ORGANIZATION.COMMUNICATION_TEMPLATES.EDIT_TITLE", {
                    name: activeEmailTemplate.name,
                  })
                }}
              </template>
              <template v-else>
                {{
                  $t("ORGANIZATION.COMMUNICATION_TEMPLATES.CREATE_TITLE", {
                    type: $t(
                      "ORGANIZATION.COMMUNICATION_TEMPLATES.EMAIL_CUSTOMIZATION"
                    ),
                  })
                }}
              </template>
            </lf-h1>
          </div>
        </div>
        <div class="flex items-center space-x-4">
          <template-action-buttons
            class="flex items-stretch space-x-4"
            form="emailTemplateForm"
            :is-edit="isEdit"
            :show-draft="
              activeEmailTemplate?.status !== TEMPLATE_MODES.published
            "
            :is-submitting="isSubmitting"
            :is-published="
              activeEmailTemplate?.status === TEMPLATE_MODES.published
            "
            @delete="showDeleteModal = true"
            @save-draft="saveTemplate(TEMPLATE_MODES.draft)"
            @publish="saveTemplate(TEMPLATE_MODES.published)"
          >
            <template v-if="lastUpdated" #lastUpdated>
              {{ lastUpdated }}
            </template>
          </template-action-buttons>
        </div>
      </div>
    </div>
  </div>

  <view-wrapper class="px-20 xl:px-50">
    <form id="emailTemplateForm" autocomplete="off">
      <lf-card has-padding class="mb-8mb-8 space-y-4 py-6">
        <lf-h3>
          {{
            $t("ORGANIZATION.COMMUNICATION_TEMPLATES.DETAIL", {
              type: $t("ORGANIZATION.COMMUNICATION_TEMPLATES.EMAIL.TITLE"),
            })
          }}
        </lf-h3>
        <lf-input
          data-cy="email-template-name"
          name="name"
          :placeholder="$t('COMMON.TEMPLATE_NAME')"
          :value="activeEmailTemplate?.name"
        />
        <lf-textarea
          data-cy="email-template-description"
          name="description"
          :placeholder="$t('COMMON.DESCRIPTION')"
          :value="activeEmailTemplate?.description || ''"
          :character-limit="TEXT_CHARACTER_LIMIT_DEFAULT"
          noresize
          rows="2"
        />
      </lf-card>
      <lf-card has-padding class="space-y-4 py-6">
        <div class="flex items-center justify-between">
          <lf-h3>
            {{
              $t("ORGANIZATION.COMMUNICATION_TEMPLATES.CONTENT", {
                type: $t(
                  "ORGANIZATION.COMMUNICATION_TEMPLATES.EMAIL.SHORT_NAME"
                ),
              })
            }}
          </lf-h3>
          <div class="flex items-center space-x-2">
            <icon-base
              v-if="emailWarningMessage"
              v-tooltip="emailWarningMessage"
              :icon="IconWarning"
              data-cy="cc-warning"
              class="text-orange-400"
            />
            <primary-button @click="sendTestEmail">
              {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.SEND_EMAIL") }}
            </primary-button>
          </div>
        </div>
        <div class="relative">
          <span
            v-if="values.subject"
            class="absolute top-0 left-0 z-1 -mt-3 ml-2-5 bg-white px-1 text-xs leading-6"
          >
            {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.SUBJECT") }}
          </span>
          <dynamic-field
            :model-value="activeEmailTemplate?.subject || ''"
            name="subject"
            data-cy="email-template-subject"
            class="email-headless"
            :placeholder="$t('ORGANIZATION.COMMUNICATION_TEMPLATES.SUBJECT')"
          />
        </div>
        <template v-if="!loading">
          <div class="relative">
            <span
              class="absolute top-0 left-0 z-1 -mt-3 ml-2-5 bg-white px-1 text-xs leading-6"
            >
              {{ $t("ORGANIZATION.COMMUNICATION_TEMPLATES.CC") }}
            </span>
            <lf-email-input-multiple
              :value="ccInput"
              :emails="activeEmailTemplate?.cc || []"
              :dynamic-fields="emailDynamicFields"
              @change="handleCcChange"
            />
          </div>
          <div class="relative">
            <span
              class="absolute top-0 left-0 z-1 -mt-3 ml-2-5 bg-white px-1 text-xs leading-6"
            >
              {{ $t("EMAIL_INTEGRATION.BCC") }}
            </span>
            <lf-email-input-multiple
              :value="bccInput"
              :emails="activeEmailTemplate?.bcc || []"
              :dynamic-fields="emailDynamicFields"
              @change="handleBccChange"
            />
          </div>
        </template>
        <div class="relative">
          <span
            v-if="values.intro"
            class="absolute top-0 left-0 z-1 -mt-3 ml-2-5 bg-white px-1 text-xs leading-6"
          >
            {{ $t("COMMON.TITLE") }}
          </span>
          <dynamic-field
            :model-value="activeEmailTemplate?.intro || ''"
            data-cy="email-template-intro"
            name="intro"
            class="email-headless"
            :placeholder="$t('COMMON.TITLE')"
          />
        </div>
        <div v-if="!loading" class="email-templates-edit flex flex-col">
          <dynamic-field
            data-cy="email-template-body"
            class="dynamic-field__body"
            name="body"
            :model-value="activeEmailTemplate?.body || ''"
            show-toolbar
            allow-shortcuts
            toolbar-type="dynamic"
          />
          <div class="bg-disabled p-2">
            {{ t("COMMON.TYPE") }}
            <span class="bg-white px-2 py-1">[</span>
            {{
              t(
                "ORGANIZATION.COMMUNICATION_TEMPLATES.DYNAMIC_FIELD_SYMBOL_EXPLANATION"
              ).toLowerCase()
            }}
          </div>
        </div>
      </lf-card>
    </form>
  </view-wrapper>

  <confirm-modal
    v-if="showDeleteModal"
    :title="$t('ORGANIZATION.COMMUNICATION_TEMPLATES.DELETE_TITLE')"
    :description="$t('ORGANIZATION.COMMUNICATION_TEMPLATES.DELETE_DESCRIPTION')"
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    :close="() => (showDeleteModal = false)"
    @confirm="handleDelete"
  />
  <send-email-modal
    v-if="showSendEmailModal"
    :test-data="testData"
    @close="showSendEmailModal = false"
    @email:send="sendEmail"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";
import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";

import { AnalyticsEvent } from "@/enums/analytics";
import { getObjectKeys } from "@/helpers/common";
import { areValuesEqual } from "@/helpers/communicationTemplates";
import { TEXT_CHARACTER_LIMIT_DEFAULT } from "@/helpers/constants";
import { TEMPLATE_MODES } from "@/helpers/constants/emailTemplates";
import { formatDateCustom } from "@/helpers/formatting";
import {
  removeAttributesFromPayload,
  removeExtraPropertiesFromPayload,
} from "@/helpers/textEditor";
import { callStoreAction } from "@/helpers/vee-validate";
import { useEmailDynamicFields } from "@/composables/useEmailDynamicFields";
import { usePromiseWrapper } from "@/hooks/common";
import { useNotification } from "@/hooks/notifications";
import router from "@/router";
import {
  ROUTE_EMAIL_TEMPLATES,
  ROUTE_EMAIL_TEMPLATES_EDIT,
} from "@/router/routes";
import customizationApiService from "@/services/modules/customization";
import { useAnalyticsStore } from "@/stores/analytics";
import useCustomizationStore from "@/stores/customization";

import DynamicField from "@/components/DynamicField.vue";
import IconWarning from "@/components/icons/IconWarning.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import TemplateActionButtons from "@/components/ui/TemplateActionButtons.vue";
import BackButton from "@/components/ui/buttons/BackButton.vue";
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";
import LfEmailInputMultiple from "@/components/ui/inputs/LfEmailInputMultiple.vue";
import SendEmailModal from "@/views/deals/components/emails/SendEmailModal.vue";

import type { EmailTemplate, IClient } from "@/models/clients";
import type {
  SendEmailModalData,
  TestEmailData,
  TestEmailPayload,
} from "@/models/emails";
import type { DynamicFieldOptions } from "@/models/options";

enum EmailTemplateDynamicField {
  subject = "subject",
  intro = "intro",
  body = "body",
}

const values = reactive<EmailTemplate>({
  id: "",
  created_at: "",
  updated_at: "",
  name: "",
  description: "",
  intro: "",
  subject: "",
  cc: [],
  bcc: [],
  body: "",
  footer: "",
});

const route = useRoute();
const { t } = useI18n();
const store = useCustomizationStore();
const { activeEmailTemplate } = storeToRefs(store);
const { getters, dispatch } = useStore();
const { trackEvent } = useAnalyticsStore();

const { showMessage } = useNotification();

const { fetchWrapper: getEmailTemplate, loading } = usePromiseWrapper(
  async () => {
    if (!isEdit.value) {
      store.activeEmailTemplate = { ...values };
      return;
    }
    const id = String(route.params.templateId);
    await store.getEmailTemplate(id);
  }
);

const backRoute = ref({
  name: ROUTE_EMAIL_TEMPLATES,
});
const showDeleteModal = ref(false);
const showSendEmailModal = ref(false);
const testData = ref<TestEmailData | null>(null);
const ccInput = ref<string | null>(null);
const bccInput = ref<string | null>(null);

const dynamicFields = computed<DynamicFieldOptions | null>(
  () => getters["options/dynamicFields"]
);

const { emailDynamicFields } = useEmailDynamicFields();

const client = computed<IClient | null>(
  () => getters["auth/authClientSettings"]
);

const emailFieldsAreUpdated = computed(() =>
  getObjectKeys(EmailTemplateDynamicField).some((key) => {
    const currentValue = currentValues[key] ?? "";
    const activeTemplateValue = activeEmailTemplate.value?.[key] ?? "";
    return !areValuesEqual(currentValue, activeTemplateValue);
  })
);

const emailWarningMessage = computed(
  () =>
    (activeEmailTemplate.value?.id &&
      emailFieldsAreUpdated.value &&
      t("ORGANIZATION.COMMUNICATION_TEMPLATES.UNSAVED_CHANGES_WARNING")) ||
    ""
);

const isEdit = computed(() => route.name === ROUTE_EMAIL_TEMPLATES_EDIT);
const lastUpdated = computed(() =>
  activeEmailTemplate.value?.updated_at
    ? `${t("LOGS.LAST_UPDATED")} ${formatDateCustom(
        activeEmailTemplate.value.updated_at,
        "yyyy-MM-dd HH:mm:ss"
      )}`
    : null
);

const handleCcChange = ({
  validEmails,
  input,
}: {
  validEmails: string[];
  input: string;
}) => {
  if (!activeEmailTemplate.value) {
    return;
  }
  ccInput.value = input;
  activeEmailTemplate.value.cc = validEmails;
};

const handleBccChange = ({
  validEmails,
  input,
}: {
  validEmails: string[];
  input: string;
}) => {
  if (!activeEmailTemplate.value) {
    return;
  }
  bccInput.value = input;
  activeEmailTemplate.value.bcc = validEmails;
};

const { fetchWrapper: handleDelete } = usePromiseWrapper(async () => {
  if (!isEdit.value || !route.params.templateId) {
    return;
  }
  await customizationApiService.deleteEmailTemplate(
    route.params.templateId.toString()
  );
  trackEvent(AnalyticsEvent.EmailTemplateDeleted, {
    template_id: route.params.templateId.toString(),
  });
  await router.push(backRoute.value);
});

const {
  handleSubmit,
  isSubmitting,
  values: currentValues,
} = useForm({
  initialValues: { ...values },
});

const { fetchWrapper: sendEmail } = usePromiseWrapper(
  async (data: SendEmailModalData) => {
    const payload: TestEmailPayload = {
      ...data,
      subject: activeEmailTemplate.value?.subject || "",
      intro: activeEmailTemplate.value?.intro || "",
      body: activeEmailTemplate.value?.body || "",
      bcc: activeEmailTemplate.value?.bcc || [],
      cc: activeEmailTemplate.value?.cc || [],
    };

    await customizationApiService.sendEmailTestData(payload);
    showSendEmailModal.value = false;
  }
);

const sendTestEmail = async () => {
  if (activeEmailTemplate.value?.id) {
    showSendEmailModal.value = true;
    return;
  }

  const values = {
    subject: currentValues.subject,
    body: currentValues.body,
    name: currentValues.name,
  };

  const emptyFields: string[] = [];

  getObjectKeys(values).forEach((key) => {
    if (isEmpty(values[key])) {
      emptyFields.push(key);
    }
  });

  if (emptyFields.length) {
    showMessage(
      t(
        "ORGANIZATION.COMMUNICATION_TEMPLATES.SEND_EMAIL_NO_DATA_MESSAGE",
        {
          fields: emptyFields.join(", "),
        },
        emptyFields.length
      ),
      "warning"
    );
    return;
  }

  await saveTemplate(TEMPLATE_MODES.draft);
  showSendEmailModal.value = true;
};

const saveTemplate = (
  status: (typeof TEMPLATE_MODES)[keyof typeof TEMPLATE_MODES]
) =>
  handleSubmit(async (values, actions) => {
    const payload: Partial<EmailTemplate> & {
      client_id?: string;
      dynamic_field_dropdown?: string;
      search?: string;
    } = {
      ...values,
    };

    if (activeEmailTemplate.value?.cc) {
      payload.cc = activeEmailTemplate.value.cc;
    }

    if (activeEmailTemplate.value?.bcc) {
      payload.bcc = activeEmailTemplate.value.bcc;
    }

    if (isEdit.value && activeEmailTemplate.value?.id) {
      payload.id = activeEmailTemplate.value.id;
    }

    if (client.value?.id) {
      payload.client_id = client.value.id;
    }

    getObjectKeys(EmailTemplateDynamicField).forEach((key) => {
      payload[key] = removeAttributesFromPayload(payload[key]);
    });

    removeExtraPropertiesFromPayload(payload);

    const actionName = isEdit.value
      ? "updateEmailTemplate"
      : "createEmailTemplate";

    payload.status = status;
    try {
      await callStoreAction(payload, actions, actionName, store);
      if (isEdit.value) {
        trackEvent(AnalyticsEvent.EmailTemplateUpdated, {
          template_id: activeEmailTemplate.value?.id,
        });
      } else {
        trackEvent(AnalyticsEvent.EmailTemplateCreated, {
          template_id: store.activeEmailTemplate?.id,
        });
      }
    } catch (error) {
      // The error message will be displayed, no need to show 422 error
      if ((error as { message: string })?.message?.includes("422")) {
        return;
      }
      const message =
        (error as { message: string })?.message ?? t("COMMON.ERROR_OCCURRED");
      showMessage(message, "error");
    }
    if (!isEdit.value) {
      if (!store.activeEmailTemplate?.id) {
        return;
      }
      await router.push({
        name: ROUTE_EMAIL_TEMPLATES_EDIT,
        params: {
          templateId: store.activeEmailTemplate?.id,
        },
      });
    }
  })();

getEmailTemplate();

onBeforeMount(async () => {
  if (isEmpty(dynamicFields.value?.fields)) {
    await dispatch("options/getDynamicFields");
  }
  testData.value = await customizationApiService.getEmailTestData();
});

onBeforeUnmount(() => {
  store.activeEmailTemplate = null;
});
</script>
